import { motion } from 'framer-motion'
import { ArrowRightIcon, CheckIcon, BoltIcon, CogIcon, ChartBarIcon, ClockIcon } from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'

export default function Automatizacion() {
  const features = [
    {
      icon: BoltIcon,
      title: "Workflows Inteligentes",
      description: "Automatización de procesos empresariales con lógica adaptativa"
    },
    {
      icon: CogIcon,
      title: "Integración de Sistemas",
      description: "Conectamos diferentes plataformas y herramientas empresariales"
    },
    {
      icon: ChartBarIcon,
      title: "Análisis y Optimización",
      description: "Monitoreo continuo y mejora de procesos automatizados"
    },
    {
      icon: ClockIcon,
      title: "Ahorro de Tiempo",
      description: "Reducción significativa del tiempo en tareas repetitivas"
    }
  ]

  const solutions = [
    "Automatización de marketing y ventas",
    "Gestión automatizada de inventarios",
    "Procesamiento automático de documentos",
    "Workflows de aprobación y notificaciones",
    "Integración de CRM y ERP",
    "Automatización de reportes y análisis"
  ]

  const tools = [
    { name: "Zapier", category: "No-Code" },
    { name: "Make", category: "Integration" },
    { name: "Python", category: "Scripting" },
    { name: "Power Automate", category: "Microsoft" },
    { name: "RPA Tools", category: "Automation" },
    { name: "API Integration", category: "Custom" },
    { name: "Webhooks", category: "Real-time" },
    { name: "Cloud Functions", category: "Serverless" }
  ]

  const benefits = [
    {
      metric: "80%",
      label: "Reducción en tiempo de tareas manuales"
    },
    {
      metric: "95%",
      label: "Precisión en procesos automatizados"
    },
    {
      metric: "24/7",
      label: "Operación continua sin intervención"
    },
    {
      metric: "60%",
      label: "Aumento en productividad del equipo"
    }
  ]

  const industries = [
    {
      name: "E-commerce",
      description: "Automatización de pedidos, inventario y atención al cliente"
    },
    {
      name: "Finanzas",
      description: "Procesamiento de facturas, conciliación y reportes financieros"
    },
    {
      name: "Recursos Humanos",
      description: "Onboarding, gestión de vacaciones y evaluaciones de desempeño"
    },
    {
      name: "Marketing",
      description: "Campañas automáticas, lead scoring y nurturing de prospectos"
    }
  ]

  return (
    <div 
      className="min-h-screen"
      style={{
        background: 'linear-gradient(135deg, #0a0a0a 0%, #2e1a0a 50%, #0a0a0a 100%)',
        color: '#ffffff'
      }}
    >
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div 
            className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl"
            style={{ background: 'rgba(251, 146, 60, 0.1)' }}
          />
          <div 
            className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl"
            style={{ background: 'rgba(245, 158, 11, 0.1)' }}
          />
        </div>

        {/* Gear Animation */}
        <div className="absolute inset-0 opacity-20">
          {[...Array(12)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: '24px',
                height: '24px'
              }}
              animate={{
                rotate: 360,
                scale: [1, 1.2, 1]
              }}
              transition={{
                duration: 8 + Math.random() * 4,
                repeat: Infinity,
                ease: "linear",
                delay: Math.random() * 2
              }}
            >
              <CogIcon 
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  color: '#fb923e' 
                }} 
              />
            </motion.div>
          ))}
        </div>

        <div className="relative pt-32 pb-20 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  style={{
                    display: 'inline-block',
                    padding: '8px 20px',
                    background: 'rgba(251, 146, 60, 0.2)',
                    border: '1px solid rgba(251, 146, 60, 0.4)',
                    borderRadius: '25px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#fb923e',
                    marginBottom: '24px'
                  }}
                >
                  Automatización
                </motion.div>
                
                <motion.h1
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  style={{
                    fontSize: '48px',
                    fontWeight: '800',
                    lineHeight: '1.1',
                    color: '#ffffff',
                    marginBottom: '24px'
                  }}
                  className="lg:text-6xl"
                >
                  Automatiza tu{' '}
                  <span 
                    style={{
                      background: 'linear-gradient(135deg, #fb923e, #f59e0b)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}
                  >
                    empresa
                  </span>
                </motion.h1>
                
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  style={{
                    fontSize: '20px',
                    lineHeight: '1.6',
                    color: '#d1d5db',
                    marginBottom: '32px'
                  }}
                  className="text-xl lg:text-2xl"
                >
                  Optimizamos tus procesos empresariales con automatización inteligente 
                  que reduce costos, elimina errores y libera tiempo valioso para tu equipo.
                </motion.p>
                
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="flex flex-col sm:flex-row gap-4"
                >
                  <Link to="/contacto">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      style={{
                        padding: '16px 32px',
                        background: 'linear-gradient(135deg, #fb923e, #f59e0b)',
                        color: '#ffffff',
                        fontWeight: '600',
                        borderRadius: '12px',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Automatizar Ahora
                      <ArrowRightIcon style={{ width: '20px', height: '20px' }} />
                    </motion.button>
                  </Link>
                  <Link to="/casos">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      style={{
                        padding: '16px 32px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '12px',
                        color: '#ffffff',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '16px',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Ver Resultados
                    </motion.button>
                  </Link>
                </motion.div>
              </motion.div>

              {/* Right Visual */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative flex items-center justify-center"
              >
                <div className="relative">
                  {/* Central Gear */}
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    style={{
                      width: '200px',
                      height: '200px',
                      background: 'linear-gradient(135deg, #fb923e, #f59e0b)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}
                  >
                    <CogIcon 
                      style={{ 
                        width: '120px', 
                        height: '120px', 
                        color: '#ffffff' 
                      }} 
                    />
                  </motion.div>

                  {/* Orbiting Smaller Gears */}
                  {[0, 90, 180, 270].map((rotation, index) => (
                    <motion.div
                      key={index}
                      className="absolute"
                      style={{
                        top: '-40px',
                        left: '50%',
                        transformOrigin: '0 140px',
                        marginLeft: '-40px'
                      }}
                      animate={{ rotate: rotation }}
                      transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                    >
                      <motion.div
                        style={{
                          width: '80px',
                          height: '80px',
                          background: 'rgba(251, 146, 60, 0.8)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        animate={{ rotate: -rotation * 1.5 }}
                        transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                      >
                        <CogIcon 
                          style={{ 
                            width: '48px', 
                            height: '48px', 
                            color: '#ffffff' 
                          }} 
                        />
                      </motion.div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 
              style={{
                fontSize: '36px',
                fontWeight: '700',
                color: '#ffffff',
                marginBottom: '16px'
              }}
              className="text-4xl lg:text-5xl"
            >
              Características Clave
            </h2>
            <p 
              style={{
                fontSize: '18px',
                color: '#d1d5db',
                maxWidth: '800px',
                margin: '0 auto'
              }}
              className="text-xl"
            >
              Implementamos automatización inteligente que se adapta y evoluciona 
              con las necesidades específicas de tu negocio
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -10 }}
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '16px',
                    padding: '32px',
                    textAlign: 'center',
                    transition: 'all 0.3s ease'
                  }}
                  className="group hover:border-orange-500/30"
                >
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    style={{
                      width: '64px',
                      height: '64px',
                      background: 'linear-gradient(135deg, #fb923e, #f59e0b)',
                      borderRadius: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 24px auto'
                    }}
                  >
                    <IconComponent 
                      style={{ 
                        width: '32px', 
                        height: '32px', 
                        color: '#ffffff' 
                      }} 
                    />
                  </motion.div>
                  <h3 
                    style={{
                      fontSize: '20px',
                      fontWeight: '600',
                      color: '#ffffff',
                      marginBottom: '12px'
                    }}
                  >
                    {feature.title}
                  </h3>
                  <p 
                    style={{
                      fontSize: '16px',
                      color: '#d1d5db',
                      lineHeight: '1.6'
                    }}
                  >
                    {feature.description}
                  </p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section 
        className="py-20 px-6"
        style={{
          background: 'rgba(255, 255, 255, 0.02)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 
                style={{
                  fontSize: '36px',
                  fontWeight: '700',
                  color: '#ffffff',
                  marginBottom: '24px'
                }}
                className="text-4xl lg:text-5xl"
              >
                Soluciones que Ofrecemos
              </h2>
              <p 
                style={{
                  fontSize: '18px',
                  color: '#d1d5db',
                  marginBottom: '32px'
                }}
                className="text-xl"
              >
                Automatizamos procesos clave que transformarán la eficiencia 
                operacional de tu empresa
              </p>
              
              <div className="space-y-4">
                {solutions.map((solution, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '16px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}
                  >
                    <CheckIcon 
                      style={{ 
                        width: '24px', 
                        height: '24px', 
                        color: '#fb923e',
                        flexShrink: 0
                      }} 
                    />
                    <span 
                      style={{
                        fontSize: '16px',
                        color: '#ffffff',
                        fontWeight: '500'
                      }}
                    >
                      {solution}
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              {/* Tools Grid */}
              <div 
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '24px',
                  padding: '48px'
                }}
              >
                <h3 
                  style={{
                    fontSize: '24px',
                    fontWeight: '600',
                    color: '#ffffff',
                    marginBottom: '24px',
                    textAlign: 'center'
                  }}
                >
                  Herramientas de Automatización
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {tools.map((tool, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      whileHover={{ scale: 1.05 }}
                      style={{
                        padding: '16px',
                        background: 'linear-gradient(135deg, rgba(251, 146, 60, 0.1), rgba(245, 158, 11, 0.1))',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '12px',
                        textAlign: 'center',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <h4 
                        style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#ffffff',
                          marginBottom: '4px'
                        }}
                      >
                        {tool.name}
                      </h4>
                      <span 
                        style={{
                          fontSize: '12px',
                          color: '#fb923e',
                          fontWeight: '500',
                          padding: '2px 8px',
                          background: 'rgba(251, 146, 60, 0.2)',
                          borderRadius: '6px'
                        }}
                      >
                        {tool.category}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 
              style={{
                fontSize: '36px',
                fontWeight: '700',
                color: '#ffffff',
                marginBottom: '16px'
              }}
              className="text-4xl lg:text-5xl"
            >
              Resultados Comprobados
            </h2>
            <p 
              style={{
                fontSize: '18px',
                color: '#d1d5db',
                maxWidth: '800px',
                margin: '0 auto'
              }}
              className="text-xl"
            >
              Nuestros clientes experimentan mejoras significativas en eficiencia 
              y productividad después de implementar automatización
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                style={{
                  background: 'linear-gradient(135deg, rgba(251, 146, 60, 0.1), rgba(245, 158, 11, 0.1))',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(251, 146, 60, 0.3)',
                  borderRadius: '16px',
                  padding: '32px',
                  textAlign: 'center'
                }}
              >
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  style={{
                    fontSize: '48px',
                    fontWeight: '800',
                    color: '#fb923e',
                    marginBottom: '12px'
                  }}
                  className="text-5xl lg:text-6xl"
                >
                  {benefit.metric}
                </motion.div>
                <p 
                  style={{
                    fontSize: '16px',
                    color: '#ffffff',
                    fontWeight: '500',
                    lineHeight: '1.5'
                  }}
                >
                  {benefit.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section 
        className="py-20 px-6"
        style={{
          background: 'rgba(255, 255, 255, 0.02)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 
              style={{
                fontSize: '36px',
                fontWeight: '700',
                color: '#ffffff',
                marginBottom: '16px'
              }}
              className="text-4xl lg:text-5xl"
            >
              Industrias que Atendemos
            </h2>
            <p 
              style={{
                fontSize: '18px',
                color: '#d1d5db',
                maxWidth: '800px',
                margin: '0 auto'
              }}
              className="text-xl"
            >
              Automatización especializada para diferentes sectores empresariales
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '16px',
                  padding: '32px',
                  transition: 'all 0.3s ease'
                }}
              >
                <h3 
                  style={{
                    fontSize: '24px',
                    fontWeight: '600',
                    color: '#fb923e',
                    marginBottom: '16px'
                  }}
                >
                  {industry.name}
                </h3>
                <p 
                  style={{
                    fontSize: '16px',
                    color: '#d1d5db',
                    lineHeight: '1.6'
                  }}
                >
                  {industry.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            style={{
              background: 'linear-gradient(135deg, rgba(251, 146, 60, 0.2), rgba(245, 158, 11, 0.2))',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '24px',
              padding: '48px'
            }}
          >
            <h2 
              style={{
                fontSize: '36px',
                fontWeight: '700',
                color: '#ffffff',
                marginBottom: '16px'
              }}
              className="text-4xl lg:text-5xl"
            >
              ¿Listo para automatizar?
            </h2>
            <p 
              style={{
                fontSize: '20px',
                color: '#d1d5db',
                marginBottom: '32px',
                maxWidth: '600px',
                margin: '0 auto 32px auto'
              }}
            >
              Liberemos a tu equipo de tareas repetitivas para que se enfoquen en lo que realmente importa
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contacto">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    padding: '16px 32px',
                    background: 'linear-gradient(135deg, #fb923e, #f59e0b)',
                    color: '#ffffff',
                    fontWeight: '600',
                    borderRadius: '12px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  Iniciar Automatización
                  <ArrowRightIcon style={{ width: '20px', height: '20px' }} />
                </motion.button>
              </Link>
              <Link to="/casos">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    padding: '16px 32px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    color: '#ffffff',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '16px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  Ver Casos de Éxito
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
